<script>
  import Layout from '../components/Layout.svelte';
  import { onMount, tick } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { createCurrencyFormatter } from '../lib/currency.js';
  import { user } from '../stores/authStore';

  // Single source of truth
  let allBills = [];
  
  // Bill settings & bank accounts for per-row transaction account selection
  let billSettings = {};
  let xeroBankAccounts = [];   // [{ accountID, name, code }]
  let billBankAccounts = {};   // map: bill.id → accountID (per-row override)

  // UI state
  let loading = true;
  let error = null;
  let selectedBillIDs = new Set();
  let hideSynced = true; // Default: hide synced bills
  let billsAndExpenses = false; // Show bills only (false) or bills + expenses (true); persisted in user settings
  let searchQuery = ""; // Search filter
  let checkingSyncStatus = false; // Overall sync check status
  
  // Pagination state
  let currentPage = 1;
  let pageSize = 10;

  // Syncing state
  let syncing = false;
  let syncProgress = { current: 0, total: 0 };
  let syncResults = [];
  
  // Modal state for mark as synced confirmation
  let showMarkSyncedModal = false;
  let pendingMarkSyncedIds = [];
  
  // Reference to sync results section for auto-scrolling
  let syncResultsSection;

  // Reactive computed views
  $: showPaymentAccountCol = billsAndExpenses && xeroBankAccounts.length > 0;
  $: filteredBills = getFilteredBills(allBills, hideSynced, searchQuery, billsAndExpenses);
  $: visibleBills = filteredBills.slice(0, currentPage * pageSize);
  $: totalItems = filteredBills.length;
  $: totalPages = Math.ceil(totalItems / pageSize);
  // Both bills and expenses are selectable when not synced
  $: selectableFilteredBills = visibleBills.filter(bill => !bill.isSynced);
  $: allSelectableAreSelected = selectableFilteredBills.length > 0 && 
    selectableFilteredBills.every(bill => selectedBillIDs.has(bill.id));
  $: someSelected = selectableFilteredBills.some(bill => selectedBillIDs.has(bill.id));
  $: canSyncSelected = selectedBillIDs.size > 0 && !syncing;
  $: canMarkSelected = selectedBillIDs.size > 0 && !syncing;

  function getFilteredBills(bills, hideSync, search, showExpenses) {
    let filtered = [...bills];
    
    // Apply bills vs bills+expenses filter (client-side; server always returns both)
    if (!showExpenses) {
      filtered = filtered.filter(b => b.itemType === 'vendorBill');
    }
    
    // Apply hide synced filter (affects both bills and expenses)
    if (hideSync) {
      filtered = filtered.filter(b => !b.isSynced);
    }
    
    // Apply search filter (min 2 chars)
    if (search && search.trim().length >= 2) {
      const query = search.toLowerCase();
      filtered = filtered.filter(b => 
        (b.number && String(b.number).toLowerCase().includes(query)) ||
        (b.vendor?.name && b.vendor.name.toLowerCase().includes(query)) ||
        (b.job?.name && b.job.name.toLowerCase().includes(query)) ||
        (b.description && b.description.toLowerCase().includes(query))
      );
    }
    
    return filtered;
  }

  async function fetchBills() {
    try {
      loading = true;
      error = null;
      
      const params = new URLSearchParams();
      params.set('status', 'approved');
      params.set('size', '100');
      params.set('maxTotal', '500'); // Backend paginates documents until this cap
      
      console.log('🚀 Fetching vendor bills from JobTread');
      
      const response = await fetch(`/api/jt/vendor-bills?${params.toString()}`);
      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401 && data.needsLogin) {
          error = 'Please configure your JobTread credentials in Settings';
          return;
        }
        throw new Error(data.error || 'Failed to fetch bills check Job tread Credentials in Settings or Date Ranges are valid');
      }

      if (!data.success || !Array.isArray(data.bills)) {
        throw new Error('Invalid data format received from server');
      }

      console.log(`✅ Fetched ${data.bills.length} items from JobTread (bills + expenses)`);
      
      // Store items with initial sync status (itemType: vendorBill | expense)
      allBills = data.bills.map(bill => ({
        ...bill,
        itemType: bill.itemType === 'expense' ? 'expense' : 'vendorBill',
        isSynced: false,
        syncDetails: null,
        syncStatusChecked: false
      }));
      
      // Check sync status for all bills using batch endpoint
      await checkBillsSyncStatus();
      
    } catch (err) {
      console.error('❌ Error fetching bills:', err);
      error = err.message;
    } finally {
      loading = false;
    }
  }

  async function checkBillsSyncStatus() {
    if (allBills.length === 0) return;
    
    checkingSyncStatus = true;
    
    try {
      const billIds = allBills.filter(b => b.itemType === 'vendorBill').map(b => b.id);
      const expenseIds = allBills.filter(b => b.itemType === 'expense').map(b => b.id);
      
      if (billIds.length === 0 && expenseIds.length === 0) {
        checkingSyncStatus = false;
        return;
      }

      console.log(`🔍 Checking sync status for ${billIds.length} bills, ${expenseIds.length} expenses using batch endpoint`);
      
      const response = await fetch('/api/jt/check-vendor-bills-sync-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ billIds, expenseIds })
      });
      
      if (response.ok) {
        const data = await response.json();
        const syncStatuses = data.syncStatuses || {};
        
        console.log('📋 Received sync statuses for', Object.keys(syncStatuses).length, 'items');
        
        // Update all items with sync status (bills and expenses both get status from sync-tracking table)
        allBills = allBills.map(item => {
          const status = syncStatuses[item.id];
          return {
            ...item,
            isSynced: status?.isSynced || false,
            syncDetails: status?.isSynced ? {
              xeroBillId: status.targetId,
              xeroBillNumber: status.targetNumber,
              syncedAt: status.syncedAt
            } : null,
            syncStatusChecked: true
          };
        });
        
        const syncedCount = allBills.filter(b => b.isSynced).length;
        console.log(`✅ Sync check complete: ${syncedCount} synced, ${allBills.length - syncedCount} need sync`);
        initBillBankAccountDefaults();
      } else {
        console.error('Failed to check sync status, assuming all need sync');
      }
    } catch (error) {
      console.error('❌ Error checking sync status:', error);
    } finally {
      checkingSyncStatus = false;
    }
  }

  function handleSearch(event) {
    searchQuery = event.target.value;
    currentPage = 1; // Reset to first page on search
  }

  function handleRetry() {
    window.location.reload();
  }

  async function loadBillsAndExpensesSetting() {
    try {
      const response = await fetch('/api/users/integrations');
      if (response.ok) {
        const data = await response.json();
        billsAndExpenses = data.billSettings?.billsAndExpenses ?? false;
        billSettings = data.billSettings || {};
      }
    } catch (err) {
      console.warn('Could not load billsAndExpenses setting, using default (bills only):', err);
    }
  }

  async function loadXeroBankAccounts() {
    try {
      const response = await fetch('/api/xero/bank-accounts');
      if (response.ok) {
        const data = await response.json();
        if (data.success && Array.isArray(data.accounts)) {
          xeroBankAccounts = data.accounts;
        }
      }
    } catch (err) {
      console.warn('Could not load Xero bank accounts:', err);
    }
  }

  function initBillBankAccountDefaults() {
    const defaultAccountID = billSettings.xeroExpenseBankAccountID || xeroBankAccounts[0]?.accountID || '';
    allBills.forEach(b => {
      if (b.itemType === 'expense' && !b.isSynced && !billBankAccounts[b.id]) {
        billBankAccounts[b.id] = defaultAccountID;
      }
    });
    billBankAccounts = { ...billBankAccounts };
  }

  async function handleBillsAndExpensesChange(event) {
    const checked = event.target.checked;
    billsAndExpenses = checked;
    try {
      const response = await fetch('/api/users/bill-settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ billsAndExpenses: checked })
      });
      if (!response.ok) {
        console.error('Failed to save billsAndExpenses setting');
        billsAndExpenses = !checked; // Revert
      }
    } catch (err) {
      console.error('Failed to save billsAndExpenses setting:', err);
      billsAndExpenses = !checked; // Revert
    }
  }

  onMount(async () => {
    await Promise.all([loadBillsAndExpensesSetting(), loadXeroBankAccounts()]);
    await fetchBills();
    initBillBankAccountDefaults();
  });

  // Xero already has a bill from this supplier with this invoice number; the
  // user has checked it and says this one is different: create it anyway.
  // A crash or timeout returns an HTML error page, not JSON. Safari then threw
  // "The string did not match the expected pattern" with no hint of what to do.
  async function readSyncResponse(response) {
    try {
      return await response.json();
    } catch (_) {
      return {
        success: false,
        error: `Buildzilla's server didn't answer properly (HTTP ${response.status}). Check Xero before retrying: the bill may already have been created.`
      };
    }
  }

  async function createBillAnyway(result) {
    result.creating = true;
    syncResults = [...syncResults];
    try {
      const response = await fetch('/api/jt/sync-vendor-bill-to-xero', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...result.retryBody, allowDuplicateSupplierInvoice: true })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) throw new Error(data.error || 'Failed to sync to Xero');
      Object.assign(result, { success: true, error: null, duplicateSupplierInvoice: false, xeroBill: data.xeroBill, mappingsUsed: data.mappingsUsed || {} });
      const billIndex = allBills.findIndex(b => b.id === result.item.id);
      if (billIndex !== -1) {
        allBills[billIndex] = { ...allBills[billIndex], isSynced: true, syncStatusChecked: true, syncDetails: { xeroBillId: data.xeroBill.id, xeroBillNumber: data.xeroBill.number, syncedAt: new Date().toISOString() } };
        allBills = [...allBills];
      }
    } catch (err) {
      result.error = err.message;
    } finally {
      result.creating = false;
      syncResults = [...syncResults];
    }
  }

  function formatDate(dateString) {
    return new Date(dateString).toLocaleDateString();
  }

  $: formatCurrency = createCurrencyFormatter($user?.country);

  async function toggleBillSelection(billID) {
    console.log('Toggling bill:', billID);
    if (billID === undefined) {
      console.error('Received undefined billID');
      return;
    }
    selectedBillIDs = new Set(selectedBillIDs);
    if (selectedBillIDs.has(billID)) {
      selectedBillIDs.delete(billID);
    } else {
      selectedBillIDs.add(billID);
    }
    console.log('Selected bill IDs after toggle:', Array.from(selectedBillIDs));
  }

  function handleRowClick(bill) {
    if (bill.isSynced) return;
    toggleBillSelection(bill.id);
  }

  async function syncSelected() {
    // Split selected items into bills and expenses (different handling)
    const selectedItems = visibleBills.filter(b => selectedBillIDs.has(b.id));
    const selectedBills = selectedItems.filter(b => b.itemType === 'vendorBill');
    const selectedExpenses = selectedItems.filter(b => b.itemType === 'expense');

    if (selectedItems.length === 0) {
      console.log('No items selected');
      return;
    }

    syncing = true;
    syncProgress = { current: 0, total: selectedBills.length + selectedExpenses.length };
    syncResults = [];

    await tick();
    if (syncResultsSection) {
      syncResultsSection.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    // 1. Sync vendor bills (existing flow)
    for (let i = 0; i < selectedBills.length; i++) {
      const bill = selectedBills[i];
      try {
        console.log(`Syncing bill ${bill.number} (${bill.id}) to Xero... (${i + 1}/${selectedBills.length})`);

        const response = await fetch('/api/jt/sync-vendor-bill-to-xero', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ jtBillId: bill.id })
        });

        const result = await readSyncResponse(response);

        if (response.ok && result.success) {
          syncResults.push({
            item: bill,
            itemType: 'vendorBill',
            success: true,
            xeroBill: result.xeroBill,
            mappingsUsed: result.mappingsUsed || {}
          });

          const billIndex = allBills.findIndex(b => b.id === bill.id);
          if (billIndex !== -1) {
            allBills[billIndex] = {
              ...allBills[billIndex],
              isSynced: true,
              syncStatusChecked: true,
              syncDetails: {
                xeroBillId: result.xeroBill.id,
                xeroBillNumber: result.xeroBill.number,
                syncedAt: new Date().toISOString()
              }
            };
            allBills = [...allBills];
          }
        } else {
          syncResults.push({
            item: bill,
            itemType: 'vendorBill',
            success: false,
            error: result.error || 'Unknown error',
            duplicateSupplierInvoice: result.duplicateSupplierInvoice === true,
            retryBody: { jtBillId: bill.id }
          });
        }
      } catch (error) {
        syncResults.push({
          item: bill,
          itemType: 'vendorBill',
          success: false,
          error: error.message
        });
      }

      syncProgress.current++;
      syncResults = [...syncResults];
      if (i < selectedBills.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 20));
      }
    }

    // 2. Expense sync (bill + payment to Xero)
    for (let i = 0; i < selectedExpenses.length; i++) {
      const expense = selectedExpenses[i];
      try {
        console.log(`Syncing expense ${expense.number} (${expense.id}) to Xero... (${i + 1}/${selectedExpenses.length})`);

        const response = await fetch('/api/jt/sync-vendor-bill-to-xero', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ jtBillId: expense.id, itemType: 'expense', bankAccountID: billBankAccounts[expense.id] || '' })
        });

        const result = await readSyncResponse(response);

        if (response.ok && result.success) {
          syncResults.push({
            item: expense,
            itemType: 'expense',
            success: true,
            xeroBill: result.xeroBill,
            mappingsUsed: result.mappingsUsed || {}
          });

          const billIndex = allBills.findIndex(b => b.id === expense.id);
          if (billIndex !== -1) {
            allBills[billIndex] = {
              ...allBills[billIndex],
              isSynced: true,
              syncStatusChecked: true,
              syncDetails: {
                xeroBillId: result.xeroBill.id,
                xeroBillNumber: result.xeroBill.number,
                syncedAt: new Date().toISOString()
              }
            };
            allBills = [...allBills];
          }
        } else {
          syncResults.push({
            item: expense,
            itemType: 'expense',
            success: false,
            error: result.error || 'Unknown error',
            duplicateSupplierInvoice: result.duplicateSupplierInvoice === true,
            retryBody: { jtBillId: expense.id, itemType: 'expense', bankAccountID: billBankAccounts[expense.id] || '' }
          });
        }
      } catch (error) {
        syncResults.push({
          item: expense,
          itemType: 'expense',
          success: false,
          error: error.message
        });
      }

      syncProgress.current++;
      syncResults = [...syncResults];
      if (i < selectedExpenses.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 20));
      }
    }

    syncing = false;
    selectedBillIDs = new Set();
  }

  function markSelectedAsSynced() {
    const selectedBillsArray = Array.from(selectedBillIDs);
    
    if (selectedBillsArray.length === 0) {
      console.log('No items selected for manual sync marking');
      return;
    }

    // Show confirmation modal
    pendingMarkSyncedIds = selectedBillsArray;
    showMarkSyncedModal = true;
  }

  async function confirmMarkAsSynced() {
    showMarkSyncedModal = false;
    
    if (pendingMarkSyncedIds.length === 0) {
      return;
    }

    try {
      console.log(`Marking ${pendingMarkSyncedIds.length} item(s) as synced...`);
      
      // Build items array with itemType so the server records the correct sync type
      // (JTBILL for vendor bills, JTEXPENSE for expenses)
      const items = pendingMarkSyncedIds.map(id => {
        const bill = allBills.find(b => b.id === id);
        return { id, itemType: bill?.itemType || 'vendorBill' };
      });

      const response = await fetch('/api/jt/mark-bills-as-synced', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        console.log(`✅ Successfully marked ${result.successCount} item(s) as synced`);
        
        // Update allBills to mark selected items as synced
        allBills = allBills.map(bill => {
          if (pendingMarkSyncedIds.includes(bill.id)) {
            return {
              ...bill,
              isSynced: true,
              syncStatusChecked: true,
              syncDetails: {
                xeroBillId: '00000000-0000-0000-0000-000000000000',
                xeroBillNumber: 'MANUAL-SYNC',
                syncedAt: new Date().toISOString()
              }
            };
          }
          return bill;
        });
        
        // Clear selection
        selectedBillIDs = new Set();
        
        // Trigger reactivity
        allBills = [...allBills];
      } else {
        console.error('❌ Failed to mark bills as synced:', result.error);
        alert(`Failed to mark bills as synced: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Error marking bills as synced:', error);
      alert(`Error marking bills as synced: ${error.message}`);
    } finally {
      pendingMarkSyncedIds = [];
    }
  }

  function cancelMarkAsSynced() {
    showMarkSyncedModal = false;
    pendingMarkSyncedIds = [];
  }

  function handleModalBackdropClick(e) {
    if (e.target === e.currentTarget) {
      cancelMarkAsSynced();
    }
  }

  function toggleSelectAll() {
    if (allSelectableAreSelected) {
      // Deselect all
      selectableFilteredBills.forEach(bill => selectedBillIDs.delete(bill.id));
    } else {
      // Select all selectable bills
      selectableFilteredBills.forEach(bill => selectedBillIDs.add(bill.id));
    }
    selectedBillIDs = new Set(selectedBillIDs);
  }

  async function handleSyncAllClick() {
    const allOnPageNeedingSync = selectableFilteredBills;
    if (allOnPageNeedingSync.length === 0) {
      alert('No bills or expenses on this page need syncing.');
      return;
    }
    allOnPageNeedingSync.forEach(bill => selectedBillIDs.add(bill.id));
    selectedBillIDs = new Set(selectedBillIDs);
    await syncSelected();
  }
</script>

<Layout activeRoute="sync-bills-from-jobtread">
  <div class="container mx-auto px-4 py-8">
    <h1 class="text-2xl font-bold mb-6">Import Vendor Bills and Expenses from JobTread to Xero</h1>
    <p class="text-sm text-gray-600 mb-4">Showing vendor bills (Approved or Pending) and expenses from JobTread</p>
    
    {#if loading}
      <div class="flex justify-center items-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    {:else if error}
      <div class="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded flex items-center justify-between">
        <span class="text-sm">{error}</span>
        <button
          on:click={handleRetry}
          class="ml-4 bg-blue-600 text-white text-sm px-3 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
        >
          Retry
        </button>
      </div>
    {:else if allBills.length === 0}
      <div class="flex items-center justify-between bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded">
        <p class="text-sm">No vendor bills or expenses found in JobTread.</p>
        <button
          on:click={handleRetry}
          class="ml-4 bg-blue-600 text-white text-sm px-3 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
        >
          Refresh
        </button>
      </div>
    {:else}
      <!-- Help Note for Multiple Xero Codes -->
      <div class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
        <p class="text-sm text-blue-800">
          📖 <strong>Multiple Xero Codes:</strong> 
          JobTread Vendor bill line items can be synced to multiple Xero Account codes. 
          <a href="/settings/xero-mapping" class="text-blue-600 underline hover:text-blue-800">See mappings here</a>
        </p>
      </div>
      <div class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
        <p class="text-sm text-blue-800">
          📖 <strong>Sync with Xero Category:</strong> 
          If you wish to sync to a custom tracking category, you can configure the tracking category in Advanced Bill Settings. 
          <a href="/settings" class="text-blue-600 underline hover:text-blue-800">See here</a>
        </p>
      </div>

      <div class="bg-white rounded-lg shadow overflow-hidden">
        <div class="p-4 border-b">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-6">
              <label class="inline-flex items-center cursor-pointer">
                <input 
                  type="checkbox" 
                  bind:checked={hideSynced}
                  class="sr-only peer focus:outline-none focus-visible:outline-none focus:ring-0" 
                >
                <div class="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                <span class="ms-3 text-sm font-medium text-gray-900">
                  Hide Synced
                  <span class="text-xs text-gray-500 font-normal">
                    ({allBills.filter(b => b.isSynced).length} synced, {allBills.filter(b => !b.isSynced).length} need sync)
                  </span>
                </span>
              </label>
              <label class="inline-flex items-center cursor-pointer gap-2">
                <span class="text-sm font-medium {!billsAndExpenses ? 'text-gray-900' : 'text-gray-500'}">Show Bills Only</span>
                <input 
                  type="checkbox" 
                  checked={billsAndExpenses}
                  on:change={handleBillsAndExpensesChange}
                  class="sr-only peer focus:outline-none focus-visible:outline-none focus:ring-0" 
                >
                <div class="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                <span class="text-sm font-medium {billsAndExpenses ? 'text-gray-900' : 'text-gray-500'}">Show Bills and Expenses</span>
              </label>
            </div>
            <div class="flex flex-wrap gap-2 items-center">
              <button 
                on:click={markSelectedAsSynced} 
                disabled={!canMarkSelected} 
                class="inline-flex items-center justify-center h-9 px-4 text-sm font-medium rounded-md whitespace-nowrap transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400 bg-white text-gray-600 border border-gray-300 hover:bg-[#f0f4ff] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Mark as Synced ({selectedBillIDs.size})
              </button>
              <button 
                on:click={syncSelected} 
                disabled={!canSyncSelected} 
                class="inline-flex items-center justify-center h-9 px-4 text-sm font-medium rounded-md whitespace-nowrap transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {syncing ? `Syncing ${syncProgress.current}/${syncProgress.total}…` : `Sync Selected (${selectedBillIDs.size})`}
              </button>
              <button 
                on:click={handleSyncAllClick} 
                disabled={syncing} 
                class="inline-flex items-center justify-center h-9 px-4 text-sm font-medium rounded-md whitespace-nowrap transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-green-500 bg-green-600 text-white hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Sync All
              </button>
            </div>
          </div>
        </div>

        <!-- Search Filter Section -->
        <div class="p-4 pb-8 border-b bg-gray-50">
          <div class="relative">
            <div class="relative">
              <!-- Magnifying glass icon -->
              <svg class="absolute left-3 top-2.5 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search by number, vendor, job, or description (min. 2 chars)..."
                class="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm {searchQuery && searchQuery.trim().length === 1 ? 'border-yellow-400' : ''}"
                value={searchQuery}
                on:input={handleSearch}
              />
              {#if searchQuery && searchQuery.trim().length === 1}
                <div class="absolute -bottom-6 left-0 text-xs text-yellow-600">
                  Please enter at least 2 characters to search
                </div>
              {/if}
            </div>
            {#if searchQuery}
              <button
                class="absolute right-3 top-2 text-gray-400 hover:text-gray-600"
                on:click={() => {
                  searchQuery = "";
                  currentPage = 1;
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
              </button>
            {/if}
          </div>
        </div>

        <!-- Status Info -->
        <div class="flex items-center justify-center px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
          <p class="text-sm text-gray-700">
            Showing
            <span class="font-medium">{visibleBills.length}</span>
            of
            <span class="font-medium">{totalItems}</span>
            results
          </p>
        </div>

        <!-- Select All -->
        <div class="px-4 py-3 bg-white border-b border-gray-200">
          <div class="flex items-center">
            <input
              type="checkbox"
              class="form-checkbox h-5 w-5 text-primary"
              checked={allSelectableAreSelected}
              on:change={toggleSelectAll}
              disabled={selectableFilteredBills.length === 0}
            >
            <span class="ml-2 text-gray-700">Select All</span>
          </div>
        </div>

        {#if visibleBills.length === 0}
          <div class="p-8 text-center text-gray-500">
            <p class="text-lg">
              {#if searchQuery}
                No items match your search for "{searchQuery}"
              {:else if hideSynced && allBills.every(b => b.isSynced)}
                All items are synced to Xero
              {:else}
                No bills approved in JT and not synced to Xero
              {/if}
            </p>
          </div>
        {:else}
          <div class="overflow-x-auto">
            <table class="min-w-full table-fixed">
              <thead class="bg-white">
                <tr>
                  <th class="w-[5%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Select</th>
                  <th class="w-[8%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Type</th>
                  <th class="w-[10%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Number</th>
                  <th class="w-[18%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Vendor</th>
                  <th class="w-[15%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Job</th>
                  <th class="w-[8%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Date</th>
                  <th class="w-[8%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Due Date</th>
                  <th class="w-[8%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Total</th>
                  {#if showPaymentAccountCol}
                  <th class="w-[12%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Xero Transaction Account</th>
                  {/if}
                  <th class="w-[8%] px-2 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">Sync Status</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-[#edf0f7]">
                {#each visibleBills as bill (bill.id)}
                <tr class="hover:bg-[#f0f4ff] {bill.isSynced ? 'bg-gray-50 opacity-75' : 'cursor-pointer'}" on:click={() => handleRowClick(bill)}>
                  <td class="px-2 py-3 whitespace-nowrap text-sm" on:click={(e) => e.stopPropagation()}>
                    <input 
                      type="checkbox" 
                      class="form-checkbox h-4 w-4 text-primary {bill.isSynced ? 'opacity-50 cursor-not-allowed' : ''}"
                      checked={selectedBillIDs.has(bill.id)}
                      on:change={() => toggleBillSelection(bill.id)}
                      disabled={bill.isSynced}
                    />
                  </td>
                  <td class="px-2 py-3 whitespace-nowrap text-sm">
                    {#if bill.itemType === 'expense'}
                      <span class="px-1.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">Expense</span>
                    {:else}
                      <span class="px-1.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">Bill</span>
                    {/if}
                  </td>
                  <td class="px-2 py-3 whitespace-nowrap text-sm truncate">{bill.number || 'N/A'}</td>
                  <td class="px-2 py-3 whitespace-nowrap text-sm truncate">{bill.vendor?.name || 'N/A'}</td>
                  <td class="px-2 py-3 whitespace-nowrap text-sm truncate">{bill.job?.name || '—'}</td>
                  <td class="px-2 py-3 whitespace-nowrap text-sm">{formatDate(bill.date || bill.createdAt)}</td>
                  <td class="px-2 py-3 whitespace-nowrap text-sm">{bill.dueDate ? formatDate(bill.dueDate) : '—'}</td>
                  <td class="px-2 py-3 whitespace-nowrap text-sm">{formatCurrency(bill.total || 0)}</td>
                  {#if showPaymentAccountCol}
                  <td class="px-2 py-3 text-sm" on:click={(e) => e.stopPropagation()}>
                    {#if bill.itemType === 'expense' && !bill.isSynced}
                      <select
                        class="text-xs border border-gray-300 rounded px-1 py-1 w-full"
                        bind:value={billBankAccounts[bill.id]}
                      >
                        {#each xeroBankAccounts as acct (acct.accountID)}
                          <option value={acct.accountID}>{acct.name}</option>
                        {/each}
                      </select>
                    {:else}
                      <span class="text-gray-400">—</span>
                    {/if}
                  </td>
                  {/if}
                  <td class="px-2 py-3 whitespace-nowrap text-sm">
                    {#if checkingSyncStatus && !bill.syncStatusChecked}
                      <span class="px-1.5 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Checking...
                      </span>
                    {:else if bill.isSynced}
                      <span class="px-1.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        Synced
                      </span>
                    {:else}
                      <span class="px-1.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                        Needs Sync
                      </span>
                    {/if}
                  </td>
                </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}

        <!-- Show More Button -->
        {#if currentPage < totalPages}
          <div class="flex items-center justify-center px-4 py-4 bg-white border-t border-gray-200">
            <button
              on:click={() => currentPage++}
              disabled={syncing}
              class="relative inline-flex items-center px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Show More ({Math.min(pageSize, totalItems - visibleBills.length)} more)
            </button>
          </div>
        {/if}

        <!-- Sync Results -->
        {#if syncing || syncResults.length > 0}
          <div class="p-4 border-t bg-gray-50" bind:this={syncResultsSection}>
            <h3 class="text-lg font-medium text-gray-900 mb-3">Sync Results</h3>
            {#if syncResults.length === 0 && syncing}
              <div class="flex items-center justify-center py-4 text-gray-600">
                <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
                <span>Starting sync...</span>
              </div>
            {:else}
              <div class="space-y-2">
                {#each syncResults as result}
                {@const item = result.item}
                <div class="flex items-start py-2 px-3 rounded gap-3 {result.success ? 'bg-green-50' : result.stub ? 'bg-amber-50' : 'bg-red-50'}">
                  <div class="flex-shrink-0 w-48">
                    <span class="text-sm {result.success ? 'text-green-800' : result.stub ? 'text-amber-800' : 'text-red-800'}">
                      {item.number || item.id} - {item.vendor?.name}
                      {#if result.itemType === 'expense'}
                        <span class="ml-1 px-1.5 text-xs font-semibold rounded bg-purple-100 text-purple-700">Expense</span>
                      {/if}
                    </span>
                  </div>
                  {#if result.success}
                    <div class="flex items-start justify-between flex-1 gap-2">
                      <div class="flex flex-col gap-1">
                        <span class="text-xs text-green-600">✅ Synced → {result.xeroBill.number}</span>
                        {#if result.mappingsUsed && Object.keys(result.mappingsUsed).length > 0}
                          <span class="text-xs text-blue-600">
                            Mapped: {Object.entries(result.mappingsUsed).map(([code, info]) =>
                              `${code} (${info.costCodeName}) → ${info.xeroAccountCode}`
                            ).join(', ')}
                          </span>
                        {/if}
                        {#if result.xeroBill.tracking?.length}
                          {#each result.xeroBill.tracking as entry}
                            <span class="text-xs text-purple-600">
                              🏷️ Tracking: {entry.categoryName} = {entry.option}
                            </span>
                          {/each}
                        {:else if result.xeroBill.trackingCategory}
                          <span class="text-xs text-purple-600">
                            🏷️ Tracking: {result.xeroBill.trackingCategory.name} = {result.xeroBill.trackingCategory.value}
                          </span>
                        {/if}
                        {#if result.xeroBill.trackingWarning}
                          <span class="text-xs text-amber-700">⚠️ {result.xeroBill.trackingWarning}</span>
                        {/if}
                      </div>
                      <div class="flex items-center gap-2 flex-shrink-0">
                        {#if item.job?.id}
                          <a
                            href={`https://app.jobtread.com/jobs/${item.job.id}/documents/${item.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            class="bg-blue-500 hover:bg-blue-600 text-white text-xs px-3 py-1 rounded transition-colors duration-200 whitespace-nowrap"
                          >
                            Open in JobTread →
                          </a>
                        {/if}
                        <a
                          href={result.xeroBill.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          class="bg-blue-500 hover:bg-blue-600 text-white text-xs px-3 py-1 rounded transition-colors duration-200 whitespace-nowrap"
                        >
                          Open in Xero →
                        </a>
                      </div>
                    </div>
                  {:else if result.stub}
                    <span class="text-xs text-amber-700">⏳ {result.error}</span>
                  {:else}
                    <span class="text-xs text-red-600">❌ {result.error}</span>
                    {#if result.duplicateSupplierInvoice}
                      <button
                        class="ml-2 bg-white border border-red-300 text-red-700 hover:bg-red-50 text-xs px-2.5 py-0.5 rounded disabled:opacity-50"
                        disabled={result.creating}
                        on:click={() => createBillAnyway(result)}
                      >{result.creating ? 'Creating…' : 'Create anyway'}</button>
                    {/if}
                  {/if}
                </div>
              {/each}
              </div>
            {/if}
          </div>
        {/if}
      </div>
    {/if}
  </div>

  <!-- Mark as Synced Confirmation Modal -->
  {#if showMarkSyncedModal}
    <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" on:click={handleModalBackdropClick} on:keydown={(e) => e.key === 'Escape' && cancelMarkAsSynced()} role="button" tabindex="-1">
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6" role="dialog" aria-modal="true">
        <h3 class="text-lg font-semibold text-gray-900 mb-3">Mark as Synced</h3>
        <p class="text-gray-600 mb-4">
          Are you sure you want to mark <span class="font-semibold">{pendingMarkSyncedIds.length} bill{pendingMarkSyncedIds.length > 1 ? 's' : ''}</span> as synced?
        </p>
        <p class="text-sm text-gray-500 mb-6">
          This will prevent them from appearing as "needing sync" in the future.
        </p>
        <div class="flex gap-3 justify-end">
          <button
            on:click={cancelMarkAsSynced}
            class="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            on:click={confirmMarkAsSynced}
            class="px-4 py-2 text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
          >
            Mark as Synced
          </button>
        </div>
      </div>
    </div>
  {/if}
</Layout>

<style>
  /* Make table responsive */
  @media (max-width: 1200px) {
    .table-fixed {
      table-layout: auto;
    }
    
    td {
      white-space: normal;
      word-break: break-word;
      font-size: 0.75rem;
      padding: 0.5rem 0.25rem;
    }
    
    th {
      white-space: nowrap !important;
      font-size: 0.75rem !important;
      padding: 0.5rem 0.25rem;
    }
  }
</style>

